import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavbar from './TopNavbar';
import './PortalHome.css';

// 懒加载ECharts，减少首屏加载
const ReactECharts = lazy(() => import('echarts-for-react'));

const PortalHome = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [yesterdayData, setYesterdayData] = useState(null);
  const [qualityData, setQualityData] = useState(null);
  const [todoList, setTodoList] = useState([]);
  const [quickLinks, setQuickLinks] = useState([]);
  const [weatherData, setWeatherData] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [expandedAnnouncements, setExpandedAnnouncements] = useState(new Set());

  const toggleAnnouncement = (id) => {
    const newExpanded = new Set(expandedAnnouncements);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedAnnouncements(newExpanded);
  };

  const handlePreviewAttachment = (att) => {
    const url = att.url || att.cosUrl;
    const name = att.name || '';
    const ext = name.split('.').pop().toLowerCase();
    
    // 图片/PDF直接新窗口预览
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf'].includes(ext)) {
      // 使用 window.open 打开新窗口
      const newWindow = window.open(url, '_blank');
      // 如果弹窗被拦截，提示用户
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        alert('弹窗被浏览器拦截，请允许此网站的弹窗，或手动复制链接打开：\n' + url);
      }
    } else {
      // 其他文件类型（如docx）也尝试在新窗口打开，让浏览器决定是预览还是下载
      window.open(url, '_blank');
    }
  };
  const [todoFilter, setTodoFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [qualityDrilldown, setQualityDrilldown] = useState({ visible: false, project: '', data: [] });
  const [editingLinks, setEditingLinks] = useState(false);
  const [linksDraft, setLinksDraft] = useState([]);
  const [iconPickerIdx, setIconPickerIdx] = useState(null); // 当前正在选择图标的索引

  // 常用图标库
  const ICON_OPTIONS = [
    // 常用
    '🔗', '⭐', '📌', '🏠', '💡', '🎯', '🚀', '💎',
    // 工作
    '📝', '📊', '📈', '📋', '📁', '📂', '🗂️', '📎',
    // 技术
    '💻', '🖥️', '⚙️', '🔧', '🛠️', '🔌', '📡', '🌐',
    // 沟通
    '💬', '📞', '📧', '📨', '🔔', '📢', '🎤', '👥',
    // 学习
    '📖', '📚', '🎓', '🧠', '💭', '✏️', '📐', '🔍',
    // 业务
    '💼', '💰', '📦', '🏢', '🎁', '🛒', '📮', '🗃️',
    // 状态
    '✅', '❌', '⚠️', '🔴', '🟢', '🔵', '🟡', '⏰',
    // 其他
    '🎨', '🎵', '📷', '🎬', '🌟', '🔑', '🛡️', '🎪'
  ];

  useEffect(() => {
    // 检查登录状态
    const token = localStorage.getItem('notes_token');
    const user = localStorage.getItem('notes_realname') || localStorage.getItem('notes_user') || '用户';
    if (!token) {
      navigate('/');
      return;
    }
    setUsername(user);
    fetchPortalData();
  }, [navigate]);

  const fetchPortalData = async () => {
    try {
      setLoading(true);
      
      // 使用Promise.all并行执行所有8个API请求
      const todoParams = todoFilter ? `?filter=${todoFilter}` : '';
      const token = localStorage.getItem('notes_token');
      
      const [
        dashboardRes,
        monthRes,
        yesterdayRes,
        qualityRes,
        todoRes,
        linksRes,
        weatherRes,
        annRes
      ] = await Promise.all([
        fetch('/api/workorder/dashboard?period=year', { credentials: 'include' }).catch(() => null),
        fetch('/api/workorder/dashboard?period=month', { credentials: 'include' }).catch(() => null),
        fetch('/api/workorder/yesterday-summary', { credentials: 'include' }).catch(() => null),
        fetch('/api/workorder/quality-monitor', { credentials: 'include' }).catch(() => null),
        fetch(`/api/portal/my-todo${todoParams}`, { headers: { 'x-token': token }, credentials: 'include' }).catch(() => null),
        fetch('/api/user/quick-links', { headers: { 'x-token': token }, credentials: 'include' }).catch(() => null),
        fetch('/api/weather/current', { credentials: 'include' }).catch(() => null),
        fetch('/api/announcements', { credentials: 'include' }).catch(() => null)
      ]);

      // 处理驾驶舱数据
      if (dashboardRes?.ok) {
        const dashData = await dashboardRes.json();
        setDashboardData(dashData.data || dashData);
      } else {
        setDashboardData({ summary: { total: 0, closed: 0, unclosed: 0, closeRate: 0 } });
      }

      // 处理本月数据
      if (monthRes?.ok) {
        const monthData = await monthRes.json();
        setDashboardData(prev => ({
          ...prev,
          monthSummary: monthData.data?.summary || { total: 0 }
        }));
      }

      // 处理昨日新增
      if (yesterdayRes?.ok) {
        setYesterdayData(await yesterdayRes.json());
      } else {
        setYesterdayData({ total: 0, byProject: [] });
      }

      // 处理质量监控
      if (qualityRes?.ok) {
        setQualityData(await qualityRes.json());
      } else {
        setQualityData({ projects: [] });
      }

      // 处理待办
      if (todoRes?.ok) {
        const todoData = await todoRes.json();
        setTodoList(todoData.tasks || []);
      }

      // 处理快速链接
      if (linksRes?.ok) {
        const linksData = await linksRes.json();
        setQuickLinks(linksData.links || getDefaultQuickLinks());
      } else {
        setQuickLinks(getDefaultQuickLinks());
      }

      // 处理天气
      if (weatherRes?.ok) {
        setWeatherData(await weatherRes.json());
      }

      // 处理通知公告
      if (annRes?.ok) {
        const annData = await annRes.json();
        if (annData.success) setAnnouncements(annData.list || []);
      }
    } catch (error) {
      console.error('获取门户数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultQuickLinks = () => [];

  const handleLinkClick = (link) => {
    if (link.external || link.url.startsWith('http')) {
      window.open(link.url, '_blank');
    } else {
      navigate(link.url);
    }
  };

  // 月度趋势图配置（柱状折线组合图）
  const getTrendOption = () => {
    if (!dashboardData?.yearOverYear?.trend) return {};
    const trendData = dashboardData.yearOverYear.trend;
    const labels = trendData.map(m => m.label);
    const currentData = trendData.map(m => m.current);
    const lastYearData = trendData.map(m => m.lastYear);
    
    // 根据数据量调整标签显示
    const labelInterval = labels.length > 20 ? Math.floor(labels.length / 10) : 0;
    const labelRotate = labels.length > 15 ? 45 : 0;
    
    return {
      animation: false,
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis' },
      legend: { data: ['今年', '去年'], textStyle: { color: '#64748b', fontSize: 10 }, top: 0, itemWidth: 15, itemHeight: 10 },
      grid: { left: 40, right: 20, top: 30, bottom: 25 },
      xAxis: { 
        type: 'category', 
        data: labels, 
        axisLine: { lineStyle: { color: '#e8ecf1' } },
        axisLabel: { 
          color: '#64748b', 
          fontSize: 9,
          rotate: labelRotate,
          interval: labelInterval
        }
      },
      yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f0f2f5', type: 'dashed' } }, axisLabel: { color: '#64748b', fontSize: 9 } },
      series: [
        { 
          name: '今年', 
          type: 'bar', 
          data: currentData,
          barWidth: '45%',
          itemStyle: { 
            borderRadius: [3, 3, 0, 0],
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: '#2563eb' },
                { offset: 1, color: '#0088cc' }
              ]
            }
          },
          label: { 
            show: true, 
            position: 'top',
            color: '#2563eb',
            fontSize: 9,
            formatter: (p) => p.value > 0 ? p.value : ''
          }
        },
        { 
          name: '去年', 
          type: 'line', 
          data: lastYearData, 
          smooth: true,
          lineStyle: { color: '#f59e0b', width: 2 }, 
          itemStyle: { color: '#f59e0b' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(255,215,0,0.2)' },
                { offset: 1, color: 'rgba(255,215,0,0)' }
              ]
            }
          }
        }
      ]
    };
  };

  // 服务类型分布（玫瑰图）
  const getServiceTypeOption = () => {
    if (!dashboardData?.serviceDistribution) return {};
    const COCKPIT_COLORS = [
      '#2563eb', '#3b82f6', '#60a5fa', '#06b6d4', '#0891b2',
      '#0e7490', '#155e75', '#164e63', '#1e40af', '#1d4ed8'
    ];
    const total = dashboardData.serviceDistribution.reduce((sum, item) => sum + item.value, 0);
    return {
      animation: false,
      backgroundColor: 'transparent',
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      series: [{
        type: 'pie',
        radius: ['40%', '75%'],
        center: ['50%', '50%'],
        roseType: 'area',
        label: {
          show: true,
          formatter: (params) => {
            const pct = total > 0 ? ((params.value / total) * 100).toFixed(1) : 0;
            return `${params.name}  ${pct}%`;
          },
          color: '#64748b',
          fontSize: 11
        },
        data: dashboardData.serviceDistribution.map((item, i) => ({
          ...item,
          itemStyle: { color: COCKPIT_COLORS[i % COCKPIT_COLORS.length] }
        }))
      }]
    };
  };

  // 项目工单量条形图
  const getProjectOption = () => {
    if (!dashboardData?.projectDistribution) return {};
    const COCKPIT_COLORS = [
      '#2563eb', '#3b82f6', '#60a5fa', '#06b6d4', '#0891b2',
      '#0e7490', '#155e75', '#164e63', '#1e40af', '#1d4ed8'
    ];
    return {
      animation: false,
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: 150, right: 25, top: 8, bottom: 15 },
      xAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#f0f2f5', type: 'dashed' } },
        axisLabel: { color: '#64748b', fontSize: 10 }
      },
      yAxis: {
        type: 'category',
        data: dashboardData.projectDistribution.map(p => p.name).reverse(),
        axisLine: { lineStyle: { color: '#e8ecf1' } },
        axisLabel: { 
          color: '#64748b', 
          fontSize: 9,
          interval: 0
        }
      },
      series: [{
        type: 'bar',
        data: dashboardData.projectDistribution.map((p, i) => ({
          value: p.value,
          itemStyle: {
            borderRadius: [0, 3, 3, 0],
            color: COCKPIT_COLORS[i % COCKPIT_COLORS.length]
          }
        })).reverse(),
        barWidth: '60%',
        label: {
          show: true,
          position: 'right',
          color: '#64748b',
          fontSize: 10
        }
      }]
    };
  };

  // 模块分布玫瑰图
  const getModuleOption = () => {
    if (!dashboardData?.moduleDistribution) return {};
    const COCKPIT_COLORS = [
      '#2563eb', '#3b82f6', '#60a5fa', '#06b6d4', '#0891b2',
      '#0e7490', '#155e75', '#164e63', '#1e40af', '#1d4ed8'
    ];
    const total = dashboardData.moduleDistribution.reduce((sum, item) => sum + item.value, 0);
    return {
      animation: false,
      backgroundColor: 'transparent',
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      series: [{
        type: 'pie',
        radius: ['40%', '75%'],
        center: ['50%', '50%'],
        roseType: 'area',
        label: {
          show: true,
          formatter: (params) => {
            const pct = total > 0 ? ((params.value / total) * 100).toFixed(1) : 0;
            return `${params.name}  ${pct}%`;
          },
          color: '#64748b',
          fontSize: 11
        },
        data: dashboardData.moduleDistribution.map((item, i) => ({
          ...item,
          itemStyle: { color: COCKPIT_COLORS[i % COCKPIT_COLORS.length] }
        }))
      }]
    };
  };

  const fetchTodoList = async (filter) => {
    try {
      const token = localStorage.getItem('notes_token');
      const todoParams = filter ? `?filter=${filter}` : '';
      const todoRes = await fetch(`/api/portal/my-todo${todoParams}`, { headers: { 'x-token': token }, credentials: 'include' });
      if (todoRes.ok) {
        const todoData = await todoRes.json();
        setTodoList(todoData.tasks || []);
      }
    } catch (e) { 
      console.error('todo error:', e);
    }
  };

  const handleQualityDrilldown = async (project) => {
    try {
      const res = await fetch(`/api/workorder/quality-drilldown?project=${encodeURIComponent(project)}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setQualityDrilldown({ visible: true, project, data: data.data });
        }
      }
    } catch (e) {
      console.error('quality drilldown error:', e);
    }
  };

  const handleQualityExport = (project) => {
    window.open(`/api/workorder/quality-drilldown/export?project=${encodeURIComponent(project)}`, '_blank');
  };

  const handleQualityApprove = async (uuid, idx) => {
    try {
      const res = await fetch('/api/workorder/quality-approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ uuid })
      });
      if (res.ok) {
        // 从列表中移除已标记的工单
        setQualityDrilldown(prev => ({
          ...prev,
          data: prev.data.filter((_, i) => i !== idx)
        }));
      }
    } catch (e) {
      console.error('标记合格失败:', e);
    }
  };

  const openLinksEditor = () => {
    setLinksDraft(quickLinks.map(l => ({ ...l })));
    setEditingLinks(true);
  };

  const addLink = () => {
    setLinksDraft([...linksDraft, { id: Date.now(), name: '', icon: '🔗', url: '', external: true }]);
  };

  const updateLink = (index, field, value) => {
    const updated = [...linksDraft];
    updated[index] = { ...updated[index], [field]: value };
    setLinksDraft(updated);
  };

  const removeLink = (index) => {
    setLinksDraft(linksDraft.filter((_, i) => i !== index));
  };

  const saveLinks = async () => {
    try {
      const token = localStorage.getItem('notes_token');
      // 过滤掉名称或URL为空的
      const validLinks = linksDraft.filter(l => l.name && l.url);
      const res = await fetch('/api/user/quick-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-token': token },
        credentials: 'include',
        body: JSON.stringify({ links: validLinks })
      });
      if (res.ok) {
        setQuickLinks(validLinks);
        setEditingLinks(false);
      }
    } catch (e) {
      console.error('保存快速链接失败:', e);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 6) return '凌晨好';
    if (hour < 9) return '早上好';
    if (hour < 12) return '上午好';
    if (hour < 14) return '中午好';
    if (hour < 18) return '下午好';
    return '晚上好';
  };

  const getTodoClass = (todo) => {
    if (todo.status === 'delayed' || todo.isOverdue) return 'urgent';
    if (todo.priority === 'high') return 'high';
    return '';
  };

  const getTodoTag = (todo) => {
    if (todo.status === 'delayed' || todo.isOverdue) return <span className="todo-tag delayed">延期</span>;
    if (todo.status === 'pending') return <span className="todo-tag pending">未开始</span>;
    return <span className="todo-tag doing">进行中</span>;
  };

  const handleLogout = () => {
    localStorage.removeItem('notes_token');
    localStorage.removeItem('notes_user');
    localStorage.removeItem('notes_realname');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="portal-loading-screen">
        <div className="portal-loading">加载中...</div>
      </div>
    );
  }
  return (
    <div className="portal-container">
      <div className="portal-content">

      {/* 欢迎条 */}
      <div className="portal-welcome">
        <div className="welcome-left">
          <h1>{getGreeting()}，{username} 👋</h1>
          <p>
            今天有 {todoList.length} 项待办
            {weatherData && weatherData.temp !== '--' && (
              <span className="welcome-weather-inline">
                {' · '}
                <span className="welcome-weather-icon">{weatherData.icon}</span>
                {weatherData.temp}°C {weatherData.desc} {weatherData.city}{weatherData.rainInfo}
              </span>
            )}
          </p>
        </div>
        <div className="welcome-stats">
          <div className="w-stat">
            <div className="w-stat-val">{yesterdayData?.total || 0}</div>
            <div className="w-stat-label">昨日工单</div>
          </div>
          <div className="w-stat">
            <div className="w-stat-val">{dashboardData?.monthSummary?.total || 0}</div>
            <div className="w-stat-label">本月工单</div>
          </div>
          <div className="w-stat">
            <div className="w-stat-val">{dashboardData?.summary?.total || 0}</div>
            <div className="w-stat-label">本年工单</div>
          </div>
        </div>
      </div>

      {/* 主网格 */}
      <div className="portal-main-grid">
        {/* 左列 */}
        <div className="portal-left-col">
          {/* 工单数据驾驶舱 */}
          <div className="portal-card portal-dashboard">
            <div className="dashboard-head">
              <div className="card-title">工单数据驾驶舱</div>
              <div className="dashboard-time">更新于 {new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}</div>
            </div>
            <div className="charts-row">
              <div className="chart-box">
                <div className="chart-label">📈 月度趋势</div>
                <Suspense fallback={<div className="chart-loading">加载中...</div>}>
                  <ReactECharts option={getTrendOption()} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'canvas' }} notMerge={true} />
                </Suspense>
              </div>
              <div className="chart-box">
                <div className="chart-label">🔧 服务类型分布</div>
                <Suspense fallback={<div className="chart-loading">加载中...</div>}>
                  <ReactECharts option={getServiceTypeOption()} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'canvas' }} notMerge={true} />
                </Suspense>
              </div>
              <div className="chart-box">
                <div className="chart-label">📊 项目工单量</div>
                <Suspense fallback={<div className="chart-loading">加载中...</div>}>
                  <ReactECharts option={getProjectOption()} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'canvas' }} notMerge={true} />
                </Suspense>
              </div>
              <div className="chart-box">
                <div className="chart-label">📦 模块分布</div>
                <Suspense fallback={<div className="chart-loading">加载中...</div>}>
                  <ReactECharts option={getModuleOption()} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'canvas' }} notMerge={true} />
                </Suspense>
              </div>
            </div>
          </div>

          {/* 通知公告 + 昨日新增/工单质量 */}
          <div className="portal-row2">
            <div className="portal-card portal-announcements-large">
              <div className="card-title">📢 通知公告</div>
              <div className="announcement-list-large">
                {announcements.length === 0 ? (
                  <div className="announcement-empty">暂无公告</div>
                ) : (
                  announcements.map(ann => {
                    const isExpanded = expandedAnnouncements.has(ann.id);
                    const content = ann.content || '';
                    const shouldTruncate = content.length > 100;
                    const displayContent = shouldTruncate && !isExpanded ? content.substring(0, 100) + '...' : content;
                    
                    return (
                      <div key={ann.id} className="announcement-item-large">
                        <div className="announcement-header-large">
                          <div className="announcement-title-large">{ann.title}</div>
                          <div className="announcement-date-large">
                            {new Date(ann.createdAt).toLocaleDateString('zh-CN')}
                          </div>
                        </div>
                        <div 
                          className="announcement-content-large"
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: shouldTruncate && !isExpanded ? 2 : 'unset',
                            WebkitBoxOrient: 'vertical',
                            overflow: shouldTruncate && !isExpanded ? 'hidden' : 'visible',
                            cursor: shouldTruncate ? 'pointer' : 'default'
                          }}
                          onClick={() => shouldTruncate && toggleAnnouncement(ann.id)}
                        >
                          {displayContent}
                          {shouldTruncate && (
                            <span style={{ color: '#1890ff', fontSize: '12px', marginLeft: '4px' }}>
                              {isExpanded ? '收起' : '展开'}
                            </span>
                          )}
                        </div>
                        {ann.attachments && ann.attachments.length > 0 && (
                          <div className="announcement-attachments">
                            {ann.attachments.map((att, idx) => (
                              <a 
                                key={idx} 
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePreviewAttachment(att);
                                }}
                                className="attachment-link"
                                style={{ cursor: 'pointer' }}
                                title="点击预览/下载"
                              >
                                📎 {att.name}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            {/* 昨日新增 + 工单质量 */}
            <div className="portal-row2-right">
              <div className="portal-card portal-yesterday">
                <div className="card-title">📥 昨日新增工单</div>
                <div className="yesterday-grid">
                  {(yesterdayData?.byProject || []).map((item, idx) => (
                    <div key={idx} className="y-item">
                      <div className="y-project">{item.project}</div>
                      <div className="y-count">{item.count}</div>
                      <div className={`y-trend ${item.trend >= 0 ? 'up' : 'down'}`}>
                        {item.trend >= 0 ? '↑' : '↓'}{Math.abs(item.trend)}
                      </div>
                    </div>
                  ))}
                  <div className="y-item total">
                    <div className="y-project">合计</div>
                    <div className="y-count">{yesterdayData?.total || 0}</div>
                  </div>
                </div>
              </div>
              <div className="portal-card portal-quality">
                <div className="quality-head">
                  <div className="card-title">工单录入质量</div>
                  <div className="quality-badge">本月</div>
                </div>
                <div className="quality-cards">
                  {(qualityData?.projects || []).map((proj, idx) => (
                    <div 
                      key={idx} 
                      className={`q-card ${proj.hasIssue ? 'alert clickable' : ''}`}
                      onClick={() => proj.hasIssue && handleQualityDrilldown(proj.name)}
                      title={proj.hasIssue ? '点击查看问题工单明细' : ''}
                    >
                      <div className="q-card-head">
                        <div className={`q-card-dot ${proj.hasIssue ? 'bad' : 'ok'}`}></div>
                        <div className="q-card-name">{proj.name}</div>
                      </div>
                      <div className="q-card-stats">
                        <div className="q-stat">
                          <span className="q-stat-label">描述不清:</span>
                          <span className={`q-stat-val ${proj.unclearDesc > 0 ? 'bad' : ''}`}>{proj.unclearDesc || 0}</span>
                        </div>
                        <div className="q-stat">
                          <span className="q-stat-label">方案模糊:</span>
                          <span className={`q-stat-val ${proj.vagueSolution > 0 ? 'bad' : ''}`}>{proj.vagueSolution || 0}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!qualityData?.projects || qualityData.projects.length === 0) && (
                    <div className="quality-empty">暂无数据</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 右列 */}
        <div className="portal-right-col">
          {/* 常用功能 */}
          <div className="portal-card portal-func">
            <div className="card-title">⚡ 常用功能</div>
            <div className="func-list">
              <div className="func-btn" onClick={() => navigate('/workorder-import')}>
                <div className="func-btn-icon">📝</div>
                <div className="func-btn-text">工单录入</div>
              </div>
              <div className="func-btn" onClick={() => navigate('/work-logs/entry')}>
                <div className="func-btn-icon">📅</div>
                <div className="func-btn-text">工作日志</div>
              </div>
              <div className="func-btn" onClick={() => navigate('/knowledge/qa')}>
                <div className="func-btn-icon">🤖</div>
                <div className="func-btn-text">智能问答</div>
              </div>
              <div className="func-btn" onClick={() => navigate('/knowledge/log-analysis')}>
                <div className="func-btn-icon">📊</div>
                <div className="func-btn-text">日志分析</div>
              </div>
              <div className="func-btn" onClick={() => navigate('/projects')}>
                <div className="func-btn-icon">📁</div>
                <div className="func-btn-text">项目管理</div>
              </div>
            </div>
          </div>

          {/* 我的待办 */}
          <div className="portal-card portal-todo">
            <div className="todo-head">
              <div className="card-title">📋 我的待办</div>
              <div className="todo-filters">
                <button 
                  className={`todo-filter-btn ${todoFilter === '' ? 'active' : ''}`}
                  onClick={() => { setTodoFilter(''); fetchTodoList(''); }}
                >全部</button>
                <button 
                  className={`todo-filter-btn ${todoFilter === 'week' ? 'active' : ''}`}
                  onClick={() => { setTodoFilter('week'); fetchTodoList('week'); }}
                >本周</button>
                <button 
                  className={`todo-filter-btn ${todoFilter === 'month' ? 'active' : ''}`}
                  onClick={() => { setTodoFilter('month'); fetchTodoList('month'); }}
                >本月</button>
              </div>
              <div className="todo-more" onClick={() => navigate('/projects')}>全部 →</div>
            </div>
            <div className="todo-list">
              {todoList.slice(0, 5).map((todo, idx) => {
                const isUrgent = todo.isDueToday || todo.isOverdue;
                return (
                <div key={idx} className={`todo ${getTodoClass(todo)}`} onClick={() => navigate(`/tasks/${todo.id}`)}>
                  <div className={`todo-dot ${todo.status === 'delayed' || todo.isOverdue ? 'red' : todo.isDueToday ? 'orange' : todo.priority === 'high' ? 'orange' : 'gray'}`}></div>
                  <div className="todo-body">
                    <div className="todo-text">{todo.title}</div>
                    <div className="todo-info">
                      <span className="proj">{todo.projectName}</span>
                      <span style={isUrgent ? { color: '#ff4d4f', fontWeight: 600 } : undefined}>
                        {todo.isDueToday ? '🔴 今日到期' : todo.isOverdue ? '⚠️ 已超期' : ''} {todo.dueDate}
                      </span>
                    </div>
                  </div>
                  {getTodoTag(todo)}
                </div>
                );
              })}
              {todoList.length === 0 && <div className="todo-empty">暂无待办事项</div>}
            </div>
          </div>

          {/* 快速链接 */}
          <div className="portal-card portal-links">
            <div className="card-title">
              <span>🔗 我的快速链接</span>
              <span className="links-edit" onClick={openLinksEditor}>编辑</span>
            </div>
            <div className="links-grid">
              {quickLinks.map((link) => (
                <div key={link.id} className="link-item" onClick={() => handleLinkClick(link)} style={link.color ? { borderColor: link.color } : {}}>
                  <div className="link-icon" style={link.color ? { color: link.color } : {}}>{link.icon}</div>
                  <div className="link-name">{link.name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* 工单质量穿透弹窗 */}
      {qualityDrilldown.visible && (
        <div className="quality-modal-overlay" onClick={() => setQualityDrilldown({ visible: false, project: '', data: [] })}>
          <div className="quality-modal" onClick={e => e.stopPropagation()}>
            <div className="quality-modal-header">
              <h3>🔍 {qualityDrilldown.project} - 问题工单明细</h3>
              <div className="quality-modal-actions">
                <button className="quality-export-btn" onClick={() => handleQualityExport(qualityDrilldown.project)}>
                  📥 导出Excel
                </button>
                <button className="quality-close-btn" onClick={() => setQualityDrilldown({ visible: false, project: '', data: [] })}>
                  ✕
                </button>
              </div>
            </div>
            <div className="quality-modal-body">
              {qualityDrilldown.data.length === 0 ? (
                <div className="quality-modal-empty">暂无问题工单</div>
              ) : (
                <table className="quality-detail-table">
                  <thead>
                    <tr>
                      <th>工单编号</th>
                      <th>工单标题</th>
                      <th>问题描述</th>
                      <th>解决方案</th>
                      <th>问题类型</th>
                      <th>处理人</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {qualityDrilldown.data.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.id}</td>
                        <td title={item.title}>{item.title?.substring(0, 20) || '-'}{item.title?.length > 20 ? '...' : ''}</td>
                        <td title={item.desc}>{item.desc?.substring(0, 30) || '-'}{item.desc?.length > 30 ? '...' : ''}</td>
                        <td title={item.solution}>{item.solution?.substring(0, 30) || '-'}{item.solution?.length > 30 ? '...' : ''}</td>
                        <td><span className="quality-issue-tag">{item.issues}</span></td>
                        <td>{item.assignee || '-'}</td>
                        <td>
                          <button className="quality-approve-btn" onClick={() => handleQualityApprove(item.uuid, idx)} title="标记为合格，不再纳入质量分析">
                            ✓ 合格
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
      {/* 快速链接编辑弹窗 */}
      {editingLinks && (
        <div className="links-modal-overlay" onClick={() => setEditingLinks(false)}>
          <div className="links-modal" onClick={e => e.stopPropagation()}>
            <div className="links-modal-header">
              <h3>编辑快速链接</h3>
              <button className="quality-close-btn" onClick={() => setEditingLinks(false)}>✕</button>
            </div>
            <div className="links-modal-body">
              {linksDraft.map((link, idx) => (
                <div key={link.id || idx} className="links-edit-row">
                  <div className="links-icon-picker-wrapper">
                    <button
                      type="button"
                      className="links-edit-icon-btn"
                      onClick={() => setIconPickerIdx(iconPickerIdx === idx ? null : idx)}
                      title="点击选择图标"
                    >
                      {link.icon || '🔗'}
                    </button>
                    {iconPickerIdx === idx && (
                      <div className="links-icon-picker">
                        {ICON_OPTIONS.map((icon, i) => (
                          <button
                            key={i}
                            type="button"
                            className="links-icon-option"
                            onClick={() => {
                              updateLink(idx, 'icon', icon);
                              setIconPickerIdx(null);
                            }}
                          >
                            {icon}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    type="color"
                    className="links-edit-color"
                    value={link.color || '#3b82f6'}
                    onChange={e => updateLink(idx, 'color', e.target.value)}
                    title="选择颜色"
                  />
                  <input
                    className="links-edit-name"
                    value={link.name}
                    onChange={e => updateLink(idx, 'name', e.target.value)}
                    placeholder="名称"
                  />
                  <input
                    className="links-edit-url"
                    value={link.url}
                    onChange={e => updateLink(idx, 'url', e.target.value)}
                    placeholder="URL"
                  />
                  <label className="links-edit-external">
                    <input
                      type="checkbox"
                      checked={link.external}
                      onChange={e => updateLink(idx, 'external', e.target.checked)}
                    />
                    外部
                  </label>
                  <button className="links-edit-delete" onClick={() => removeLink(idx)}>删除</button>
                </div>
              ))}
              <button className="links-edit-add" onClick={addLink}>+ 添加链接</button>
            </div>
            <div className="links-modal-footer">
              <button className="links-edit-cancel" onClick={() => setEditingLinks(false)}>取消</button>
              <button className="links-edit-save" onClick={saveLinks}>保存</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PortalHome;
